import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate } from 'react-router-dom';
import { ApolloProvider, useQuery } from '@apollo/client';
import { client } from './apollo';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ME_QUERY } from './graphql';
import Navbar from './components/Navbar';
import AuthModal from './components/AuthModal';
import Home from './pages/Home';
import LandingPage from './pages/LandingPage';
import GroupDetail from './pages/GroupDetail';
import Profile from './pages/Profile';
import FindFolks from './pages/FindFolks';
import Notifications from './pages/Notifications';
import AdminDashboard from './pages/AdminDashboard';
import ExpertDashboard from './pages/ExpertDashboard';
import SellerDashboard from './pages/SellerDashboard';
import Learn from './pages/Learn';
import Wallet from './pages/Wallet';
import MyOrders from './pages/MyOrders';
import ServiceRequests from './pages/ServiceRequests';
import SocialPage from './pages/SocialPage';
import CompanyRegister from './pages/CompanyRegister';
import CompanyDashboard from './pages/CompanyDashboard';
import CompanyProfile from './pages/CompanyProfile';
import SiteFooter from './components/SiteFooter';
import Events from './pages/Events';
import FoodPage from './pages/FoodPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CookieConsent from './components/CookieConsent';
import OffersBanner from './components/OffersBanner';
import OnboardingFlow, { shouldShowOnboarding } from './components/OnboardingFlow';
import { LocaleProvider } from './context/LocaleContext';
import './i18n';
import './App.css';

// Handles ?join=true / ?login=true / ?role=expert deep-links from the marketing site
function DeepLinkHandler({ openAuth }) {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // On login/register success, redirect to ?role destination if present
  useEffect(() => {
    if (!currentUser) return;
    const role = sessionStorage.getItem('pendingRole');
    if (role) {
      sessionStorage.removeItem('pendingRole');
      if (role === 'expert') navigate('/expert');
      else if (role === 'seller') navigate('/seller');
    }
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (currentUser) return; // already logged in, nothing to do
    const params = new URLSearchParams(window.location.search);
    const join = params.get('join');
    const login = params.get('login');
    const role = params.get('role');

    // Stash ?role so we can redirect after auth completes
    if (role) sessionStorage.setItem('pendingRole', role);

    if (join === 'true') {
      openAuth('register');
    } else if (login === 'true') {
      openAuth('login');
    } else if (role && !join && !login) {
      // e.g. direct visit to /?role=expert without join — open register
      openAuth('register');
    }
    // Clean the query string from the URL without a page reload
    if (join || login || role) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

// Keeps currentUser in localStorage in sync with the DB — runs on every page load and every 5 min
function UserRefresher() {
  const { currentUser, updateCurrentUser, logout } = useAuth();
  const { data } = useQuery(ME_QUERY, {
    skip: !currentUser,
    fetchPolicy: 'network-only',
    pollInterval: 5 * 60 * 1000, // refresh every 5 min
  });
  useEffect(() => {
    if (data?.me) {
      updateCurrentUser(data.me); // sync fresh data from DB into auth context + localStorage
    } else if (data && data.me === null && currentUser) {
      // Token is no longer valid — clear stale session
      logout();
    }
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

function OnboardingGate() {
  const { currentUser } = useAuth();
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (currentUser) setShow(shouldShowOnboarding(currentUser));
  }, [currentUser?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!show) return null;
  return <OnboardingFlow onComplete={() => setShow(false)} />;
}

function AppContent() {
  const { currentUser } = useAuth();
  const [authModal, setAuthModal] = useState(null);
  const openAuth = (mode = 'login') => setAuthModal(mode);
  const closeAuth = () => setAuthModal(null);

  return (
    <BrowserRouter>
      <UserRefresher />
      <DeepLinkHandler openAuth={openAuth} />
      <OnboardingGate />
      <OffersBanner />
      <Navbar onAuthClick={openAuth} />
      <Routes>
        <Route path="/" element={currentUser ? <Home onAuthRequired={openAuth} /> : <LandingPage onAuth={openAuth} />} />
        <Route path="/group/:id" element={<GroupDetail onAuthRequired={openAuth} />} />
        <Route path="/profile/:id" element={<Profile />} />
        <Route path="/find-folks" element={<FindFolks onAuthRequired={openAuth} />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/expert" element={<ExpertDashboard />} />
        <Route path="/seller" element={<SellerDashboard />} />
        <Route path="/learn" element={<Learn />} />
        <Route path="/wallet" element={<Wallet onAuthRequired={openAuth} />} />
        <Route path="/orders" element={<MyOrders onAuthRequired={openAuth} />} />
        <Route path="/requests" element={<ServiceRequests onAuthRequired={openAuth} />} />
        <Route path="/social" element={<SocialPage onAuthRequired={openAuth} />} />
        <Route path="/food" element={<FoodPage onAuthRequired={openAuth} />} />
        <Route path="/events" element={<Events />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/company/register" element={<CompanyRegister />} />
        <Route path="/company/:slug/dashboard" element={<CompanyDashboard />} />
        <Route path="/company/:slug" element={<CompanyProfile />} />
      </Routes>
      {currentUser && <SiteFooter />}
      {authModal && <AuthModal mode={authModal} onClose={closeAuth} />}
      <CookieConsent />
    </BrowserRouter>
  );
}

export default function App() {
  return (
    <ApolloProvider client={client}>
      <AuthProvider>
        <LocaleProvider>
          <AppContent />
        </LocaleProvider>
      </AuthProvider>
    </ApolloProvider>
  );
}
